<template>
	<div>
		<ListLayout
			:title="$t('admin_categories_groups_label')"
			:placeholder="
				$t('admin_people_search_groups_placeholder', { OrgName: org.name })
			"
			@search="onSearch"
		>
			<template #button>
				<Button primary @click="showNewGroup = true">
					{{ $t('admin_groups_new_group_button_title') }}
				</Button>
			</template>

			<template #table>
				<Table
					:columns="columns"
					:rows="groups"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:loadingOverlay="loading"
					:loadingMore="loadingMore"
					:defaultSort="sort"
					:error="fetchError"
					headerSize="small"
					@row-click="goToGroupDetails"
					@load-more="loadMore"
					@sort="onSort"
					@retry="fetchGroups"
					loadMoreTrigger="400px"
					height="calc(100vh - 197px)"
					ref="table"
				>
					<template #cell-created_at="{ value }">
						{{ $m(value).format('L') }}
					</template>
					<template #cell-latest_user_message_created_at="{ value }">
						{{
							value
								? timeFromNow(value)
								: $t('groupsettings_option_category_uncatagorized')
						}}
					</template>
					<template #cell-category="{ value }">
						<span
							:class="{ unassigned: !value }"
							v-if="value"
							v-text="value.title"
						/>
						<Uncategorized v-if="!value" />
					</template>

					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="menuOptions"
							@click.stop
							@select="onContextMenuItemClick($event, data)"
						/>
					</template>

					<template #empty>
						<EmptyState
							v-if="!query"
							:icon="GroupsIcon"
							:text="$t('admin_groups_create_spaces_title')"
						>
							<template #button>
								<Button primary @click="showNewGroup = true">
									{{ $t('admin_groups_new_group_button_title') }}
								</Button>
							</template>
						</EmptyState>
						<NoSearchResults v-if="query" :query="query" />
					</template>
				</Table>
			</template>
		</ListLayout>

		<NewGroupModal
			v-if="showNewGroup || groupToEdit"
			ref="newModal"
			:defaultCategoryId="defaultCategoryId"
			@close="onNewGroupClose"
			:group="groupToEdit"
			@success="onNewGroupSuccess"
		/>
	</div>
</template>

<script>
import {
	Table,
	Button,
	closeAllMenus,
	DotsMenu,
	ASC,
	DSC,
} from '@/components/common'
import { GroupsIcon } from '@/icons'
import NewGroupModal from './NewGroupModal'
import debounce from 'debounce'
import {
	ListLayout,
	EmptyState,
	NoSearchResults,
	Uncategorized,
	get,
} from '../common'
import { getTimeAgoShorthand, storage } from '@/helpers/utils'
import { getOrganization } from '@/store'

const PAGE_SIZE = 50

export default {
	name: 'Groups',
	components: {
		Table,
		Button,
		DotsMenu,
		NewGroupModal,
		ListLayout,
		EmptyState,
		NoSearchResults,
		Uncategorized,
	},

	props: {
		searchTerm: String,
	},

	data() {
		return {
			groupToEdit: null,
			groups: [],
			fetchError: false,
			loading: true,
			loadingMore: false,
			showNewGroup: false,
			defaultCategoryId: null,
			GroupsIcon,
			query: this.searchTerm || null,
			sort: { field: 'title', order: ASC },
			org: {},
			columns: [
				{
					field: 'title',
					label: 'admin_groups_group_name_title',
					width: '400px',
					field: 'title',
					sorter: true,
				},
				{
					field: 'category',
					label: 'admin_common_category_title',
					width: '400px',
				},
				{
					field: 'created_at',
					label: 'admin_categories_created_on_label',
					width: '250px',
					sorter: true,
				},
				{
					field: 'latest_user_message_created_at',
					label: 'admin_groups_message_sent_label',
					width: '250px',
				},
				{
					field: 'memberships_count',
					label: 'admin_groups_members_label',
					width: '200px',
				},
				{
					field: 'actions',
					width: '100px',
					align: 'right',
				},
			],
			menuOptions: [
				{
					id: 'details',
					label: 'admin_groups_open_group_details_button',
				},
				{ id: 'edit', label: 'admin_groups_edit_group_button' },
			],
		}
	},

	created() {
		this.fetchGroupsDebounced = debounce(this.fetchGroups.bind(this), 500)
	},

	async mounted() {
		this.checkQueryParams()
		this.org = getOrganization()
		await this.fetchGroups()
	},

	async activated() {
		// if some group was modified on another screen, refetch the list
		if (storage.get('org_mgmt_changed_groups')) {
			storage.remove('org_mgmt_changed_groups')
			await this.fetchGroups()
		}
	},

	methods: {
		async onContextMenuItemClick({ id }, group) {
			switch (id) {
				case 'edit':
					this.groupToEdit = group
					break
				case 'details':
					this.goToGroupDetails(group)
					break
			}
		},

		goToGroupDetails(group) {
			closeAllMenus()
			this.$router.push({ name: 'group', params: { id: group.id, group } })
		},

		timeFromNow(dateStr) {
			const time = getTimeAgoShorthand(dateStr)
			return this.$t('common_date_relative', [time])
		},

		checkQueryParams() {
			const { query } = this.$route
			if (query.new || query.q || query.cat) {
				const newQuery = Object.assign({}, query)
				if (query.new) {
					this.showNewGroup = true
				}
				if (query.cat) {
					this.defaultCategoryId = parseInt(query.cat)
				}
				delete newQuery.new
				delete newQuery.cat
				delete newQuery.q
				this.$router.replace({ query: newQuery })
			}
		},

		onSearch(query) {
			this.query = query || null
			this.loading = true
			this.fetchGroupsDebounced()
		},

		onSort(sort, { sortParam }) {
			this.sort = {
				...sort,
				field: sortParam || sort.field,
			}
			this.fetchGroups()
		},

		async fetchGroups() {
			this.loading = true
			this.fetchError = false
			const { field, order } = this.sort
			const params = {
				include: 'category',
				'page[size]': PAGE_SIZE,
				sort: `${order === DSC ? '-' : ''}${field}`,
			}
			if (this.query) {
				params['filter[search]'] = encodeURIComponent(this.query)
			}
			try {
				this.apiResponse = await get('/organization/groups', params)
				this.groups = this.apiResponse.data
				this.loading = false
				this.$refs.table?.scrollToTop()
			} catch (e) {
				console.error(e)
				this.loading = false
				this.fetchError = true
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.groups = this.groups.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.loading = false
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},

		onNewGroupClose() {
			this.groupToEdit = null
			this.showNewGroup = false
		},
		onNewGroupSuccess() {
			this.groupToEdit = null
			this.fetchGroups()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$header-height: 170px;

header {
	background: #f9f9f9;
	height: $header-height;
	@include flex-column;
	padding: 24px;
	.title {
		@include flex-spread;
		margin: 0 0 24px 0;
		h1 {
			font-family: Avenir-Heavy;
			font-size: 36px;
			margin: 0;
			color: var(--neutral-800);
		}
	}
	.search-bar {
		background: white;
	}
}

.modal-wrapper {
	padding: 24px;
}

:deep(.pronto-table) .empty-state {
	max-width: 290px;
	text-align: center;
}
</style>
